import { useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  getAllSlackChannels,
  SlackChannel,
} from '../../../../api/users-api';
import SharedGroupedMultiSelect from '../../../../shared/shared-grouped-multi-select';
import {
  UseFormSetValue,
  UseFormWatch,
  Control,
  Controller,
} from 'react-hook-form';
import { UserFormValues } from '../user-schema';
import SlackLogo from '../../../../assets/slack-logo.svg';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { FormSectionHeader } from '../../../../shared/form-section-header';

interface StepIntegrationsProps {
  control: Control<UserFormValues>;
  setValue: UseFormSetValue<UserFormValues>;
  watch: UseFormWatch<UserFormValues>;
}

export default function StepIntegrations({ control }: StepIntegrationsProps) {
  const theme = useTheme();

  const { data: channels = [], isLoading } = useQuery({
    queryKey: [UserKeys.SlackChannels, 'all'],
    queryFn: () => getAllSlackChannels(),
    meta: {
      errorMessage:
        'There was an issue loading Slack channels. Please refresh and try again.',
    },
  });

  const options = channels.map((channel: SlackChannel) => ({
    value: channel.id,
    label: channel.name,
    groupType:
      channel.is_private || channel.isPrivate || channel.private
        ? 'Private'
        : 'Public',
  }));

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <FormSectionHeader
        description="Select integrations you'd like to apply to this user."
        sx={{ mb: 2 }}
      >
        Integrations
      </FormSectionHeader>

      <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: theme.palette.semantic.outline.light,
          borderRadius: 1,
          overflow: 'hidden',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            height: 72,
            minHeight: 72,
            backgroundColor: theme.palette.semantic.surface.bg1,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: theme.palette.semantic.outline.light,
                borderRadius: 1,
                backgroundColor: theme.palette.common.white,
              }}
            >
              <Box
                component="img"
                src={SlackLogo}
                alt="Slack Logo"
                sx={{ width: 48, height: 48 }}
              />
            </Box>
            <Stack>
              <Typography variant="body2" color="text.secondary">
                Slack
              </Typography>
              <Typography variant="subtitle1" fontWeight={500}>
                Add to Slack Channels
              </Typography>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          sx={{ p: 3, pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            This action will add newly created users to the organization's Slack
            channels.
          </Typography>

          {isLoading ? (
            <Skeleton variant="rectangular" height={200} />
          ) : (
            <>
              <Controller
                control={control}
                name="slackChannels"
                defaultValue={[]}
                render={({ field }) => (
                  <SharedGroupedMultiSelect
                    options={options}
                    value={field.value || []}
                    onChange={(val) => field.onChange(val)}
                    height={250}
                  />
                )}
              />
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
